import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import TransferOwnershipDialog from "../../components/TransferOwnershipDialog";
import ManualIssueDialog from "../../components/ManualIssueDialog";
import { toast } from "sonner";
import {
  Loader2,
  Users,
  CalendarDays,
  Receipt,
  Sliders,
  ShieldOff,
  ShieldCheck,
  RefreshCw,
  Search,
  ExternalLink,
  AlertTriangle,
  FileText,
  Package,
  Percent,
  Pencil,
  TicketPlus,
} from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import AddonsManager from "../../components/AddonsManager";
import { adminApi, centsToAUD, formatAuDate, staffOrdersApi } from "../../lib/events";
import { extractError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const TABS = [
  { key: "overview", label: "Overview", icon: Sliders },
  { key: "accounts", label: "Accounts", icon: Users },
  { key: "events", label: "Events", icon: CalendarDays },
  { key: "orders", label: "Orders & Tickets", icon: Receipt },
  { key: "config", label: "Platform config", icon: Sliders },
  { key: "audit", label: "Audit logs", icon: Receipt },
];

export default function AdminPage() {
  const { user } = useAuth();
  // Sprint AW UX fix — support ?tab=events (or accounts/config/audit) deep-links
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = ["overview", "accounts", "events", "orders", "config", "audit"].includes(searchParams.get("tab"))
    ? searchParams.get("tab") : "overview";
  const [tab, _setTab] = useState(initialTab);
  const setTab = (t) => { _setTab(t); setSearchParams(t === "overview" ? {} : { tab: t }, { replace: true }); };
  const [overview, setOverview] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [events, setEvents] = useState([]);
  const [config, setConfig] = useState(null);
  const [audit, setAudit] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [manualPreset, setManualPreset] = useState(null);

  const isAdmin = user?.is_admin;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === "overview") setOverview(await adminApi.overview());
      else if (tab === "accounts") setAccounts(await adminApi.accounts(q));
      else if (tab === "events") setEvents(await adminApi.events(q));
      else if (tab === "config") setConfig(await adminApi.getConfig());
      else if (tab === "audit") setAudit(await adminApi.auditLogs({ limit: 200 }));
      setError("");
    } catch (e) { setError(extractError(e)); }
    finally { setLoading(false); }
  }, [tab, q]);

  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  if (!isAdmin) {
    return (
      <section className="p-8" data-testid="admin-forbidden">
        <div className="max-w-md mx-auto text-center">
          <ShieldOff size={40} className="mx-auto text-[var(--gt-accent)] mb-3" />
          <h1 className="font-heading text-3xl font-black tracking-tighter mb-2">Admin only</h1>
          <p className="text-[var(--gt-ink-2)] text-sm mb-6">
            You need an admin account to view this page. Ask a platform admin to promote you via the ADMIN_EMAILS environment variable.
          </p>
          <Link to="/dashboard/events" className="gt-btn-ghost text-sm">Back to my dashboard</Link>
        </div>
      </section>
    );
  }

  return (
    <section className="p-6 sm:p-10" data-testid="admin-page">
      <div className="max-w-6xl mx-auto">
        <div className="gt-label mb-2">Getickets internal</div>
        <div className="flex items-start justify-between gap-3 flex-wrap mb-6">
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter">Admin</h1>
          <div className="flex items-center gap-2">
            <button onClick={() => setManualPreset({ organiserId: "", eventId: "", organiserLabel: "" })} className="gt-btn-primary text-sm" data-testid="admin-issue-ticket-btn">
              <TicketPlus size={14} /> Add Attendee / Issue Ticket
            </button>
            <button onClick={load} className="gt-btn-ghost text-sm" data-testid="admin-refresh-btn">
              <RefreshCw size={14} /> Refresh
            </button>
          </div>
        </div>
        {manualPreset && (
          <ManualIssueDialog open onClose={() => setManualPreset(null)} presetOrganiserId={manualPreset.organiserId} presetEventId={manualPreset.eventId} presetOrganiserLabel={manualPreset.organiserLabel} onIssued={() => load()} />
        )}

        <div className="flex items-center gap-1 border-b-2 border-[var(--gt-ink)] mb-6 overflow-x-auto">
          {TABS.map(t => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              data-testid={`admin-tab-${t.key}`}
              className={`flex items-center gap-2 px-3 py-2 text-sm font-semibold whitespace-nowrap border-b-2 -mb-0.5 ${
                tab === t.key ? "border-[var(--gt-accent)] text-[var(--gt-ink)]" : "border-transparent text-[var(--gt-ink-3)] hover:text-[var(--gt-ink)]"
              }`}
            >
              <t.icon size={14} /> {t.label}
            </button>
          ))}
        </div>

        {error && <div className="gt-card border-[var(--gt-accent)] text-[var(--gt-accent)] mb-4" data-testid="admin-error">{error}</div>}
        {loading && <div className="flex items-center gap-2 text-[var(--gt-ink-2)] text-sm" data-testid="admin-loading"><Loader2 size={14} className="animate-spin" /> Loading…</div>}

        {tab === "overview" && overview && <OverviewPanel data={overview} />}
        {tab === "accounts" && (
          <AccountsPanel accounts={accounts} q={q} setQ={setQ} onSearch={load}
                         onCreate={async () => {
                           const name = window.prompt("Organiser name:");
                           if (!name) return;
                           const email = window.prompt("Organiser email:");
                           if (!email) return;
                           try {
                             await adminApi.createAccount({ name: name.trim(), email: email.trim(), send_invite: true });
                             toast.success("Organiser created + invite sent");
                             load();
                           } catch (e) { toast.error(extractError(e)); }
                         }}
                         onSendInvite={async (id) => {
                           try {
                             await adminApi.sendInvite(id);
                             toast.success("Invite email sent");
                           } catch (e) { toast.error(extractError(e)); }
                         }}
                         onSendReset={async (id) => {
                           try {
                             await adminApi.sendPasswordReset(id);
                             toast.success("Password reset email sent");
                           } catch (e) { toast.error(extractError(e)); }
                         }}
                         onSuspend={async (id) => {
                           const reason = window.prompt("Suspend this account? Optional reason (shown in audit log):", "");
                           if (reason === null) return;
                           try {
                             await adminApi.suspend(id, reason.trim() || undefined);
                             toast.success("Account suspended");
                             load();
                           } catch (e) { toast.error(extractError(e)); }
                         }}
                         onUnsuspend={async (id) => {
                           try {
                             await adminApi.unsuspend(id);
                             toast.success("Account unsuspended");
                             load();
                           } catch (e) { toast.error(extractError(e)); }
                         }} />
        )}
        {tab === "events" && (
          <EventsPanel events={events} accounts={accounts} q={q} setQ={setQ} onSearch={load}
                       onOwnershipTransferred={load}
                       onIssueTicket={(organiserId, eventId, organiserLabel) => setManualPreset({ organiserId, eventId, organiserLabel })}
                       onUnpublish={async (id) => {
                         const reason = window.prompt("Force-unpublish this event? Reason (audit-logged):", "policy breach");
                         if (reason === null) return;
                         try {
                           await adminApi.unpublishEvent(id, reason.trim() || undefined);
                           toast.success("Event moved back to draft");
                           load();
                         } catch (e) { toast.error(extractError(e)); }
                       }} />
        )}
        {tab === "config" && config && (
          <ConfigPanel config={config} onSave={async (patch) => {
            try {
              const upd = await adminApi.patchConfig(patch);
              setConfig(upd);
              toast.success("Platform config updated");
            } catch (e) { toast.error(extractError(e)); }
          }} />
        )}
        {tab === "audit" && <AuditPanel logs={audit} />}
        {tab === "orders" && <AdminOrdersPanel />}
      </div>
    </section>
  );
}

function OverviewPanel({ data }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="admin-overview">
      <div className="gt-card">
        <div className="gt-label mb-1">Organisers</div>
        <div className="font-heading text-3xl font-black tracking-tighter">{data.organisers.total}</div>
        <div className="text-xs text-[var(--gt-ink-3)] mt-1" data-testid="admin-overview-organisers">
          {data.organisers.active} active · {data.organisers.suspended} suspended
        </div>
      </div>
      <div className="gt-card">
        <div className="gt-label mb-1">Events</div>
        <div className="font-heading text-3xl font-black tracking-tighter">{data.events.total}</div>
        <div className="text-xs text-[var(--gt-ink-3)] mt-1">{data.events.published} published</div>
      </div>
      <div className="gt-card">
        <div className="gt-label mb-1">Gross (all-time)</div>
        <div className="font-heading text-3xl font-black tracking-tighter">{centsToAUD(data.orders.gross_cents)}</div>
        <div className="text-xs text-[var(--gt-ink-3)] mt-1">{data.orders.total} orders · {centsToAUD(data.orders.refunded_cents)} refunded</div>
      </div>
    </div>
  );
}

function SearchBar({ q, setQ, onSearch, placeholder }) {
  return (
    <div className="flex items-center gap-2 mb-4">
      <div className="relative flex-1 max-w-md">
        <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-[var(--gt-ink-3)]" />
        <input
          className="gt-input pl-7"
          placeholder={placeholder}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") onSearch(); }}
          data-testid="admin-search-input"
        />
      </div>
      <button onClick={onSearch} className="gt-btn-ghost text-sm" data-testid="admin-search-btn">Search</button>
    </div>
  );
}

function AccountsPanel({ accounts, q, setQ, onSearch, onCreate, onSendInvite, onSendReset, onSuspend, onUnsuspend }) {
  const [feeEdit, setFeeEdit] = useState(null);
  const [scMode, setScMode] = useState("customer");
  const [gstMode, setGstMode] = useState("customer");
  const [gstRegistered, setGstRegistered] = useState(true);
  const [gstLabelEnabled, setGstLabelEnabled] = useState(true);
  const [allowScChoice, setAllowScChoice] = useState(false);
  const [abn, setAbn] = useState("");
  const [savingFees, setSavingFees] = useState(false);
  // Sprint BD — organiser-level service-fee rate override (admin only)
  const [useOrgRateOverride, setUseOrgRateOverride] = useState(false);
  const [orgScRate, setOrgScRate] = useState("");     // percent, e.g. "2.0"
  const [orgScFlat, setOrgScFlat] = useState("");     // dollars, e.g. "0.25"
  // Sprint AN — platform defaults dialog
  const [cfgOpen, setCfgOpen] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [savingCfg, setSavingCfg] = useState(false);

  const openFees = (a) => {
    setFeeEdit(a);
    setScMode(a.fee_settings?.service_charge_paid_by || "customer");
    setGstMode(a.fee_settings?.gst_paid_by || a.fee_settings?.tax_paid_by || "customer");
    setGstRegistered(a.fee_settings?.gst_registered !== false);
    setGstLabelEnabled(a.fee_settings?.gst_label_enabled !== false);
    // Sprint AV — default OFF unless explicitly True on the record
    setAllowScChoice(a.fee_settings?.allow_organiser_service_charge_paid_by_choice === true);
    setAbn(a.fee_settings?.abn || "");
    // Sprint BD — hydrate organiser-level rate override state
    const orgBps = a.fee_settings?.organiser_service_charge_rate_bps;
    const orgFlat = a.fee_settings?.organiser_service_charge_per_ticket_cents;
    const hasOverride = orgBps !== null && orgBps !== undefined
                        && orgFlat !== null && orgFlat !== undefined;
    setUseOrgRateOverride(hasOverride);
    setOrgScRate(hasOverride ? String(orgBps / 100) : "");
    setOrgScFlat(hasOverride ? (orgFlat / 100).toFixed(2) : "");
  };
  // Sprint AV — read platform rates dynamically from the account payload's canonical field
  const platformDisplayText = (() => {
    const pfd = feeEdit?.platform_fee_display || {};
    const pctRaw = (pfd.service_charge_rate_bps ?? 250) / 100;
    const pct = Number.isInteger(pctRaw) ? pctRaw.toFixed(1) : String(pctRaw);
    const flat = ((pfd.service_charge_per_ticket_cents ?? 30) / 100).toFixed(2);
    const gstOn = pfd.service_fee_gst_enabled !== false;
    const gstPct = ((pfd.gst_rate_bps ?? 1000) / 100).toFixed(0);
    return `${pct}% + A$${flat} per ticket · Service Fee GST ${gstOn ? `ON — ${gstPct}%` : "OFF"}`;
  })();
  const saveFees = async () => {
    // Sprint BD — validate organiser rate override when enabled
    let orgRateBps = null;
    let orgFlatCents = null;
    if (useOrgRateOverride) {
      const pct = parseFloat(orgScRate);
      const flat = parseFloat(orgScFlat);
      if ([pct, flat].some((n) => Number.isNaN(n) || n < 0) || pct > 100) {
        toast.error("Organiser rate must be a valid number (0–100%).");
        return;
      }
      orgRateBps = Math.round(pct * 100);
      orgFlatCents = Math.round(flat * 100);
    }
    setSavingFees(true);
    try {
      await adminApi.updateFeeSettings(feeEdit.id, {
        service_charge_paid_by: scMode,
        gst_paid_by: gstMode,
        gst_registered: gstRegistered,
        gst_label_enabled: gstLabelEnabled,
        allow_organiser_service_charge_paid_by_choice: allowScChoice,
        abn: abn.trim(),
        // Sprint BD — organiser-level service-fee rate override
        ...(useOrgRateOverride
          ? {
              service_charge_rate_bps: orgRateBps,
              service_charge_per_ticket_cents: orgFlatCents,
            }
          : { reset_service_fee_override: true }),
      });
      toast.success("Payment settings updated");
      setFeeEdit(null);
      onSearch();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setSavingFees(false);
    }
  };

  const openConfig = async () => {
    setCfgOpen(true);
    try {
      const c = await adminApi.getConfig();
      setCfg({
        sc_rate: String((c.service_charge_rate_bps ?? 250) / 100),
        sc_flat: String(((c.service_charge_per_ticket_cents ?? 30) / 100).toFixed(2)),
        gst_rate: String((c.gst_rate_bps ?? 1000) / 100),
        gst_enabled: c.service_fee_gst_enabled !== false,
        default_sc: c.default_service_charge_paid_by || "customer",
        default_gst: c.default_gst_paid_by || "customer",
      });
    } catch (e) {
      toast.error(extractError(e));
      setCfgOpen(false);
    }
  };
  const saveConfig = async () => {
    const scRate = parseFloat(cfg.sc_rate);
    const scFlat = parseFloat(cfg.sc_flat);
    const gstRate = parseFloat(cfg.gst_rate);
    if ([scRate, scFlat, gstRate].some((n) => Number.isNaN(n) || n < 0) || scRate > 100 || gstRate > 100) {
      toast.error("Rates must be valid numbers (0–100%).");
      return;
    }
    setSavingCfg(true);
    try {
      await adminApi.patchConfig({
        service_charge_rate_bps: Math.round(scRate * 100),
        service_charge_per_ticket_cents: Math.round(scFlat * 100),
        gst_rate_bps: Math.round(gstRate * 100),
        service_fee_gst_enabled: !!cfg.gst_enabled,
        default_service_charge_paid_by: cfg.default_sc,
        default_gst_paid_by: cfg.default_gst,
      });
      toast.success("Platform defaults updated — applies to NEW events only");
      setCfgOpen(false);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setSavingCfg(false);
    }
  };

  return (
    <div data-testid="admin-accounts-panel">
      <div className="flex items-center justify-between gap-3 mb-2">
        <SearchBar q={q} setQ={setQ} onSearch={onSearch} placeholder="Search email / name / business" />
        <div className="flex items-center gap-2">
          {/* Sprint AT dedupe — Platform defaults dialog removed from Accounts tab.
              The Platform config tab is now the single source of truth for live pricing. */}
          <button onClick={onCreate} className="gt-btn-primary text-sm" data-testid="admin-create-organiser-btn">
            + Create organiser
          </button>
        </div>
      </div>
      <div className="gt-card">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-widest text-[var(--gt-ink-3)] border-b-2 border-[var(--gt-ink)]">
              <th className="py-2 pr-3">Email</th>
              <th className="py-2 pr-3">Name</th>
              <th className="py-2 pr-3">State</th>
              <th className="py-2 pr-3">Status</th>
              <th className="py-2 pl-3 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {accounts.map(a => (
              <tr key={a.id} className="border-b border-[var(--gt-line)]" data-testid={`admin-account-row-${a.id}`}>
                <td className="py-3 pr-3">
                  <div className="font-mono text-xs">{a.email}</div>
                  {a.is_admin && <span className="text-[10px] uppercase tracking-widest text-[var(--gt-accent)] font-bold">Admin</span>}
                </td>
                <td className="py-3 pr-3">
                  <div className="font-semibold">{a.name}</div>
                  {a.business_name && <div className="text-xs text-[var(--gt-ink-3)]">{a.business_name}</div>}
                </td>
                <td className="py-3 pr-3">{a.state || "—"}</td>
                <td className="py-3 pr-3">
                  {a.is_suspended ? (
                    <span className="text-[10px] font-bold uppercase tracking-widest text-white bg-[var(--gt-accent)] px-2 py-0.5">
                      Suspended
                    </span>
                  ) : a.is_active ? (
                    <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">Active</span>
                  ) : (
                    <span className="text-[10px] font-bold uppercase tracking-widest text-[var(--gt-ink-3)]">Pending</span>
                  )}
                </td>
                <td className="py-3 pl-3 text-right">
                  <div className="inline-flex items-center gap-3">
                    {!a.is_admin && (
                      <>
                        <button onClick={() => openFees(a)} className="text-xs text-[var(--gt-ink-2)] hover:underline" data-testid={`admin-fee-settings-${a.id}`}>
                          Fees
                        </button>
                        <button onClick={() => onSendInvite(a.id)} className="text-xs text-[var(--gt-ink-2)] hover:underline" data-testid={`admin-send-invite-${a.id}`}>
                          Send invite
                        </button>
                        <button onClick={() => onSendReset(a.id)} className="text-xs text-[var(--gt-ink-2)] hover:underline" data-testid={`admin-send-reset-${a.id}`}>
                          Password reset
                        </button>
                      </>
                    )}
                    {a.is_suspended ? (
                      <button onClick={() => onUnsuspend(a.id)} className="gt-btn-ghost text-xs" data-testid={`admin-unsuspend-${a.id}`}>
                        <ShieldCheck size={12} /> Unsuspend
                      </button>
                    ) : !a.is_admin && (
                      <button onClick={() => onSuspend(a.id)} className="text-xs text-[var(--gt-accent)] hover:underline inline-flex items-center gap-1" data-testid={`admin-suspend-${a.id}`}>
                        <ShieldOff size={12} /> Suspend
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {accounts.length === 0 && (
              <tr><td colSpan={5} className="py-10 text-center text-sm text-[var(--gt-ink-3)]">No accounts.</td></tr>
            )}
          </tbody>
        </table>
      </div>

          <Dialog open={!!feeEdit} onOpenChange={(o) => !o && setFeeEdit(null)}>
        <DialogContent data-testid="admin-fee-settings-dialog">
          <DialogHeader>
            <DialogTitle>Organiser fee defaults — {feeEdit?.email}</DialogTitle>
            <DialogDescription>
              Platform service fee rates and Service Fee GST are managed by Getickets Admin.
              These organiser defaults apply to <strong>new events only</strong>. Existing events and paid orders keep their stored snapshots unless Admin edits an event directly.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5 text-sm">
            <div>
              <div className="gt-label mb-1">Platform service fee (read-only)</div>
              <div className="font-mono font-bold" data-testid="fee-platform-rate-display">
                {platformDisplayText}
              </div>
              <p className="text-[11px] text-[var(--gt-ink-3)] mt-1">
                To change platform service fee rates or Service Fee GST, use <strong>Admin → Platform config</strong>.
              </p>

              {/* Sprint BD — organiser-level rate override (admin only) */}
              <div className="mt-4 border-t border-dashed border-[var(--gt-line)] pt-4">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="gt-label mb-0.5">Organiser-level service-fee override</div>
                    <p className="text-[11px] text-[var(--gt-ink-3)]">
                      Optional. When ON, this organiser&apos;s <strong>NEW events</strong> use these rates instead of the platform default.
                      Event-level admin overrides still win over this.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setUseOrgRateOverride(!useOrgRateOverride)}
                    className={`px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] rounded-sm border transition-colors shrink-0 ml-3 ${
                      useOrgRateOverride
                        ? "bg-emerald-600 text-white border-emerald-600"
                        : "bg-white text-[var(--gt-ink-2)] border-[var(--gt-line)]"
                    }`}
                    data-testid="fee-org-rate-override-toggle"
                    aria-pressed={useOrgRateOverride}
                  >
                    {useOrgRateOverride ? "Override ON" : "Use platform default"}
                  </button>
                </div>
                {useOrgRateOverride && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                    <div>
                      <label className="gt-form-label">Service fee %</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        className="gt-input font-mono"
                        value={orgScRate}
                        onChange={(e) => setOrgScRate(e.target.value)}
                        placeholder={`${(feeEdit?.platform_fee_display?.service_charge_rate_bps ?? 250) / 100}`}
                        data-testid="fee-org-sc-rate-input"
                      />
                    </div>
                    <div>
                      <label className="gt-form-label">Service fee A$ per ticket</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className="gt-input font-mono"
                        value={orgScFlat}
                        onChange={(e) => setOrgScFlat(e.target.value)}
                        placeholder={`${((feeEdit?.platform_fee_display?.service_charge_per_ticket_cents ?? 30) / 100).toFixed(2)}`}
                        data-testid="fee-org-sc-flat-input"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <button
                        type="button"
                        onClick={() => {
                          setUseOrgRateOverride(false);
                          setOrgScRate("");
                          setOrgScFlat("");
                        }}
                        className="text-xs text-[var(--gt-ink-2)] hover:text-[var(--gt-accent)] underline"
                        data-testid="fee-org-rate-reset-btn"
                      >
                        Reset to platform default
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <label className="gt-form-label mt-4">Default service fee paid by (for new events)</label>
              <select className="gt-input" value={scMode} onChange={(e) => setScMode(e.target.value)} data-testid="fee-sc-select">
                <option value="customer">Customer pays</option>
                <option value="included">Organiser absorbs / included in ticket price</option>
              </select>
              <div className="flex items-center justify-between mt-3">
                <label className="gt-form-label mb-0">Allow organiser to choose service fee paid-by</label>
                <button
                  type="button"
                  onClick={() => setAllowScChoice(!allowScChoice)}
                  className={`px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] rounded-sm border transition-colors ${
                    allowScChoice
                      ? "bg-emerald-600 text-white border-emerald-600"
                      : "bg-white text-[var(--gt-ink-2)] border-[var(--gt-line)]"
                  }`}
                  data-testid="fee-allow-sc-toggle"
                  aria-pressed={allowScChoice}
                >
                  {allowScChoice ? "Allowed" : "Admin only"}
                </button>
              </div>
              <p className="text-[11px] text-[var(--gt-ink-3)] mt-1">
                Default: <strong>Admin only</strong>. Enable to let this organiser pick paid-by on new/editable events.
                Organisers can never edit the service fee % / per-ticket fee / GST themselves — only Admin can (via the override above).
              </p>
            </div>

            <div className="border-t border-[var(--gt-line)] pt-4">
              <div className="gt-label mb-1">Business &amp; accounting records (informational)</div>
              <p className="text-[11px] text-[var(--gt-ink-3)]">
                Service Fee GST is platform-wide and managed by Getickets. The fields below are stored on the organiser
                record for their own business/accounting reference — they do <strong>not</strong> affect buyer pricing,
                checkout totals, Stripe amount, settlement estimate, or the Service Fee GST that appears on receipts.
              </p>
              <div className="flex items-center justify-between mt-3">
                <label className="gt-form-label mb-0">Organiser GST registered (business record only)</label>
                <button
                  type="button"
                  onClick={() => setGstRegistered(!gstRegistered)}
                  className={`px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] rounded-sm border transition-colors ${
                    gstRegistered
                      ? "bg-emerald-600 text-white border-emerald-600"
                      : "bg-white text-[var(--gt-ink-2)] border-[var(--gt-line)]"
                  }`}
                  data-testid="fee-gst-registered-toggle"
                  aria-pressed={gstRegistered}
                >
                  {gstRegistered ? "GST registered" : "Not registered"}
                </button>
              </div>
              <label className="gt-form-label mt-3">ABN (business record only)</label>
              <input
                className="gt-input font-mono"
                value={abn}
                onChange={(e) => setAbn(e.target.value)}
                placeholder="11 222 333 444"
                maxLength={20}
                data-testid="fee-abn-input"
              />
              <div className="flex items-center justify-between mt-3">
                <label className="gt-form-label mb-0">
                  Store organiser GST label preference
                  <span className="ml-1 text-[10px] uppercase tracking-widest text-[var(--gt-ink-3)]">
                    · not yet rendered
                  </span>
                </label>
                <button
                  type="button"
                  onClick={() => setGstLabelEnabled(!gstLabelEnabled)}
                  className={`px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] rounded-sm border transition-colors ${
                    gstLabelEnabled
                      ? "bg-emerald-600 text-white border-emerald-600"
                      : "bg-white text-[var(--gt-ink-2)] border-[var(--gt-line)]"
                  }`}
                  data-testid="fee-gst-label-toggle"
                  aria-pressed={gstLabelEnabled}
                >
                  {gstLabelEnabled ? "Stored: On" : "Stored: Off"}
                </button>
              </div>
              <p className="text-[11px] text-[var(--gt-ink-3)] mt-1">
                Stored preference only. No receipt, email, or PDF currently renders an organiser-level GST label —
                Service Fee GST wording is generated from the platform-wide setting.
              </p>
            </div>

            <p className="text-xs text-[var(--gt-ink-3)]">
              These defaults apply to <strong>new events only</strong>. To change an existing event, use Admin → Events → Fees.
            </p>
            {feeEdit?.fee_settings?.updated_at && (
              <p className="text-xs text-[var(--gt-ink-3)]">
                Last updated {formatAuDate(feeEdit.fee_settings.updated_at)} by {feeEdit.fee_settings.updated_by_email || "admin"}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <button onClick={() => setFeeEdit(null)} className="gt-btn-ghost text-sm" data-testid="fee-cancel-btn">Cancel</button>
              <button onClick={saveFees} disabled={savingFees} className="gt-btn-primary text-sm" data-testid="fee-save-btn">
                {savingFees ? "Saving…" : "Save settings"}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Sprint AN — platform-wide fee & GST defaults */}
      <Dialog open={cfgOpen} onOpenChange={(o) => !o && setCfgOpen(false)}>
        <DialogContent data-testid="platform-config-dialog">
          <DialogHeader>
            <DialogTitle>Platform defaults</DialogTitle>
            <DialogDescription>
              Fee &amp; GST defaults for new organisers and new events. Existing events keep their snapshot.
            </DialogDescription>
          </DialogHeader>
          {!cfg ? (
            <div className="py-8 text-center text-sm text-[var(--gt-ink-3)]">Loading…</div>
          ) : (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="gt-form-label">Service charge rate (%)</label>
                  <input className="gt-input font-mono" value={cfg.sc_rate} inputMode="decimal"
                    onChange={(e) => setCfg({ ...cfg, sc_rate: e.target.value })} data-testid="cfg-sc-rate-input" />
                </div>
                <div>
                  <label className="gt-form-label">Per-ticket charge (A$)</label>
                  <input className="gt-input font-mono" value={cfg.sc_flat} inputMode="decimal"
                    onChange={(e) => setCfg({ ...cfg, sc_flat: e.target.value })} data-testid="cfg-sc-flat-input" />
                </div>
              </div>
              <div>
                <label className="gt-form-label">GST rate (%)</label>
                <input className="gt-input font-mono" value={cfg.gst_rate} inputMode="decimal"
                  onChange={(e) => setCfg({ ...cfg, gst_rate: e.target.value })} data-testid="cfg-gst-rate-input" />
                <div className="flex items-center justify-between mt-3">
                  <label className="gt-form-label mb-0">Service fee GST (platform-wide)</label>
                  <button
                    type="button"
                    onClick={() => setCfg({ ...cfg, gst_enabled: !cfg.gst_enabled })}
                    className={`px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] rounded-sm border transition-colors ${
                      cfg.gst_enabled
                        ? "bg-emerald-600 text-white border-emerald-600"
                        : "bg-white text-[var(--gt-ink-2)] border-[var(--gt-line)]"
                    }`}
                    data-testid="cfg-service-fee-gst-toggle"
                  >
                    {cfg.gst_enabled ? "GST ON" : "GST OFF"}
                  </button>
                </div>
                <p className="text-[10px] text-[var(--gt-ink-3)] mt-1">
                  GST on the Getickets service fee. Disable only if Getickets is not GST-registered.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="gt-form-label">Default: service fee paid by</label>
                  <select className="gt-input" value={cfg.default_sc}
                    onChange={(e) => setCfg({ ...cfg, default_sc: e.target.value })} data-testid="cfg-default-sc-select">
                    <option value="customer">Customer</option>
                    <option value="included">Included in ticket price</option>
                  </select>
                  <p className="text-[10px] text-[var(--gt-ink-3)] mt-1">
                    Service fee GST always follows this setting.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setCfgOpen(false)} className="gt-btn-ghost text-sm" data-testid="cfg-cancel-btn">Cancel</button>
                <button onClick={saveConfig} disabled={savingCfg} className="gt-btn-primary text-sm" data-testid="cfg-save-btn">
                  {savingCfg ? "Saving…" : "Save defaults"}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Sprint AO — admin per-event fees dialog (separate financial columns)
function AdminEventFeesDialog({ event, onClose }) {
  const [data, setData] = useState(null);
  const [prices, setPrices] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminApi
      .getEventFees(event.id)
      .then((d) => {
        setData({
          ...d,
          sc_rate: String(d.service_charge_rate_bps / 100),
          sc_flat: (d.service_charge_per_ticket_cents / 100).toFixed(2),
          gst_rate: String(d.gst_rate_bps / 100),
        });
        setPrices(Object.fromEntries(d.ticket_types.map((t) => [t.id, (t.price_cents / 100).toFixed(2)])));
      })
      .catch((e) => {
        toast.error(extractError(e));
        onClose();
      });
  }, [event.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    const scRate = parseFloat(data.sc_rate);
    const scFlat = parseFloat(data.sc_flat);
    if ([scRate, scFlat].some((n) => Number.isNaN(n) || n < 0) || scRate > 100) {
      toast.error("Rates must be valid numbers (0–100%).");
      return;
    }
    setSaving(true);
    try {
      await adminApi.updateEventPaymentSettings(event.id, {
        service_charge_paid_by: data.service_charge_paid_by,
        service_charge_rate_bps: Math.round(scRate * 100),
        service_charge_per_ticket_cents: Math.round(scFlat * 100),
      });
      for (const tt of data.ticket_types) {
        const cents = Math.round(parseFloat(prices[tt.id] || "0") * 100);
        if (!Number.isNaN(cents) && cents !== tt.price_cents) {
          await adminApi.updateTicketPrice(event.id, tt.id, cents);
        }
      }
      toast.success("Event fees updated — existing orders unchanged");
      onClose(true);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="admin-event-fees-dialog">
        <DialogHeader>
          <DialogTitle>Fees &amp; GST — {event.title}</DialogTitle>
          <DialogDescription>
            Ticket Price, Service Charge and GST are separate financial items.
          </DialogDescription>
        </DialogHeader>
        {!data ? (
          <div className="py-8 text-center text-sm text-[var(--gt-ink-3)]">Loading…</div>
        ) : (
          <div className="space-y-4 text-sm">
            {data.has_paid_orders && (
              <div className="flex items-start gap-2 text-xs font-semibold border border-[var(--gt-accent)] text-[var(--gt-accent)] p-3" data-testid="event-fees-paid-orders-warning">
                <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                This event already has paid orders. Existing orders will not change. New orders will use the updated settings.
              </div>
            )}
            <table className="w-full text-xs" data-testid="event-fees-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-[var(--gt-ink-3)] border-b-2 border-[var(--gt-ink)]">
                  <th className="py-2 pr-2">Ticket Type</th>
                  <th className="py-2 pr-2 text-right">Ticket Price</th>
                  <th className="py-2 pr-2 text-right">Service Fee %</th>
                  <th className="py-2 pr-2 text-right">Service Fee Per-Ticket</th>
                  <th className="py-2 pr-2">Service Fee Paid By</th>
                </tr>
              </thead>
              <tbody>
                {data.ticket_types.map((tt, i) => (
                  <tr key={tt.id} className="border-b border-[var(--gt-line)]" data-testid={`event-fees-row-${tt.id}`}>
                    <td className="py-2 pr-2 font-semibold">{tt.name}</td>
                    <td className="py-2 pr-2 text-right">
                      <span className="inline-flex items-center gap-1 justify-end">
                        A$
                        <input
                          className="gt-input !w-20 !py-1 text-right font-mono"
                          value={prices[tt.id] ?? ""}
                          inputMode="decimal"
                          onChange={(e) => setPrices({ ...prices, [tt.id]: e.target.value })}
                          data-testid={`event-fees-price-input-${tt.id}`}
                        />
                      </span>
                    </td>
                    {i === 0 ? (
                      <>
                        <td className="py-2 pr-2 text-right" rowSpan={data.ticket_types.length}>
                          <input
                            className="gt-input !w-16 !py-1 text-right font-mono"
                            value={data.sc_rate}
                            inputMode="decimal"
                            onChange={(e) => setData({ ...data, sc_rate: e.target.value })}
                            data-testid="event-fees-sc-rate-input"
                          />%
                        </td>
                        <td className="py-2 pr-2 text-right" rowSpan={data.ticket_types.length}>
                          A$
                          <input
                            className="gt-input !w-16 !py-1 text-right font-mono"
                            value={data.sc_flat}
                            inputMode="decimal"
                            onChange={(e) => setData({ ...data, sc_flat: e.target.value })}
                            data-testid="event-fees-sc-flat-input"
                          />
                        </td>
                        <td className="py-2 pr-2" rowSpan={data.ticket_types.length}>
                          <select
                            className="gt-input !py-1"
                            value={data.service_charge_paid_by}
                            onChange={(e) => setData({ ...data, service_charge_paid_by: e.target.value })}
                            data-testid="event-fees-sc-paid-by-select"
                          >
                            <option value="customer">Customer pays</option>
                            <option value="included">Organiser absorbs</option>
                          </select>
                        </td>
                      </>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex justify-end gap-2">
              <button onClick={() => onClose()} className="gt-btn-ghost text-sm" data-testid="event-fees-cancel-btn">Cancel</button>
              <button onClick={save} disabled={saving} className="gt-btn-primary text-sm" data-testid="event-fees-save-btn">
                {saving ? "Saving…" : "Save fees"}
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function EventsPanel({ events, accounts, q, setQ, onSearch, onUnpublish, onOwnershipTransferred, onIssueTicket }) {
  const navigate = useNavigate();
  const [feesEvent, setFeesEvent] = useState(null); // Sprint AO — per-event fees dialog
  const [addonsEvent, setAddonsEvent] = useState(null); // Sprint T1 — admin add-ons management
  const [policyEvent, setPolicyEvent] = useState(null); // event being edited
  const [policyForm, setPolicyForm] = useState({ event_terms: "", refund_policy: "", event_disclaimer: "" });
  const [policyLoading, setPolicyLoading] = useState(false);
  const [policySaving, setPolicySaving] = useState(false);
  // Sprint AW — Transfer Ownership dialog state
  const [transferEvent, setTransferEvent] = useState(null);
  // Sprint AW — Admin-Create-Event modal replacing window.prompt flow
  const [createOpen, setCreateOpen] = useState(false);
  // Sprint AW — owner filter
  const [ownerFilter, setOwnerFilter] = useState("");
  const onCreate = () => setCreateOpen(true);

  const openPolicies = async (ev) => {
    setPolicyEvent(ev);
    setPolicyLoading(true);
    try {
      const p = await adminApi.getEventPolicies(ev.id);
      setPolicyForm({
        event_terms: p.event_terms || "",
        refund_policy: p.refund_policy || "",
        event_disclaimer: p.event_disclaimer || "",
      });
    } catch (err) {
      toast.error(extractError(err));
      setPolicyEvent(null);
    } finally {
      setPolicyLoading(false);
    }
  };

  const savePolicies = async () => {
    setPolicySaving(true);
    try {
      await adminApi.updateEventPolicies(policyEvent.id, {
        event_terms: policyForm.event_terms.trim() || null,
        refund_policy: policyForm.refund_policy.trim() || null,
        event_disclaimer: policyForm.event_disclaimer.trim() || null,
      });
      toast.success("Policies updated");
      setPolicyEvent(null);
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setPolicySaving(false);
    }
  };

  return (
    <div data-testid="admin-events-panel">
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="flex items-center gap-2 flex-1">
          <SearchBar q={q} setQ={setQ} onSearch={onSearch} placeholder="Search event title / slug" />
          <input
            className="gt-input text-xs w-56"
            placeholder="Filter by Event Owner email"
            value={ownerFilter}
            onChange={(e) => setOwnerFilter(e.target.value.toLowerCase())}
            data-testid="admin-events-owner-filter"
          />
        </div>
        <button onClick={onCreate} className="gt-btn-primary text-sm" data-testid="admin-create-event-btn">
          + Create event
        </button>
      </div>
      <div className="gt-card">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-widest text-[var(--gt-ink-3)] border-b-2 border-[var(--gt-ink)]">
              <th className="py-2 pr-3">Event</th>
              <th className="py-2 pr-3">Event Owner</th>
              <th className="py-2 pr-3">Status</th>
              <th className="py-2 pr-3">Starts</th>
              <th className="py-2 pl-3 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {events
              .filter((e) => !ownerFilter || (e.organiser_email || "").toLowerCase().includes(ownerFilter))
              .map(e => (
              <tr key={e.id} className="border-b border-[var(--gt-line)]" data-testid={`admin-event-row-${e.id}`}>
                <td className="py-3 pr-3">
                  <div className="font-semibold">{e.title}</div>
                  <div className="text-[10px] font-mono text-[var(--gt-ink-3)]">{e.slug}</div>
                </td>
                <td className="py-3 pr-3" data-testid={`admin-event-owner-${e.id}`}>
                  <div className="text-xs font-semibold">{e.organiser_name || e.organiser_business_name || "—"}</div>
                  <div className="text-[10px] font-mono text-[var(--gt-ink-3)]">{e.organiser_email}</div>
                  {e.organiser_suspended && <div className="text-[10px] text-[var(--gt-accent)]">Suspended</div>}
                  {e.owner_transferred_at && (
                    <div className="text-[10px] text-[var(--gt-ink-3)]">Transferred {formatAuDate(e.owner_transferred_at)}</div>
                  )}
                </td>
                <td className="py-3 pr-3">
                  <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 border border-[var(--gt-ink)]">
                    {e.status}
                  </span>
                </td>
                <td className="py-3 pr-3 text-xs text-[var(--gt-ink-2)]">{formatAuDate(e.start_at)}</td>
                <td className="py-3 pl-3 text-right">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <button
                      onClick={() => navigate(`/dashboard/events/${e.id}/edit`)}
                      className="text-xs text-[var(--gt-accent)] font-semibold hover:underline inline-flex items-center gap-1"
                      data-testid={`admin-event-edit-${e.id}`}
                      title="Edit this event (admin override)"
                    >
                      <Pencil size={12} /> Edit
                    </button>
                    <button
                      onClick={() => navigate(`/dashboard/events/${e.id}/reports`)}
                      className="text-xs text-[var(--gt-ink-2)] hover:underline inline-flex items-center gap-1"
                      data-testid={`admin-event-reports-${e.id}`}
                      title="View event reports (orders + financials)"
                    >
                      <Receipt size={12} /> Reports
                    </button>
                    <button
                      onClick={() => navigate(`/dashboard/events/${e.id}/reports?tab=attendees`)}
                      className="text-xs text-[var(--gt-ink-2)] hover:underline inline-flex items-center gap-1"
                      data-testid={`admin-event-attendees-${e.id}`}
                      title="View event attendees / check-in status"
                    >
                      <Users size={12} /> Attendees
                    </button>
                    <button
                      onClick={() => onIssueTicket(e.organiser_id, e.id, e.organiser_business_name || e.organiser_name || e.organiser_email)}
                      className="text-xs text-[var(--gt-accent)] font-semibold hover:underline inline-flex items-center gap-1 whitespace-nowrap"
                      data-testid={`admin-event-issue-ticket-btn-${e.id}`}
                      title="Add attendee / issue ticket for this event"
                    >
                      <TicketPlus size={12} /> Add Attendee / Issue Ticket
                    </button>
                    <button
                      onClick={() => navigate(`/dashboard/events/${e.id}/team`)}
                      className="text-xs text-[var(--gt-ink-2)] hover:underline inline-flex items-center gap-1"
                      data-testid={`admin-event-team-${e.id}`}
                    >
                      <Users size={12} /> Team
                    </button>
                    <button
                      onClick={() => setFeesEvent(e)}
                      className="text-xs text-[var(--gt-ink-2)] hover:underline inline-flex items-center gap-1"
                      data-testid={`admin-edit-fees-${e.id}`}
                    >
                      <Percent size={12} /> Fees
                    </button>
                    <button
                      onClick={() => setAddonsEvent(e)}
                      className="text-xs text-[var(--gt-ink-2)] hover:underline inline-flex items-center gap-1"
                      data-testid={`admin-edit-addons-${e.id}`}
                    >
                      <Package size={12} /> Add-ons
                    </button>
                    <button
                      onClick={() => openPolicies(e)}
                      className="text-xs text-[var(--gt-ink-2)] hover:underline inline-flex items-center gap-1"
                      data-testid={`admin-edit-policies-${e.id}`}
                    >
                      <FileText size={12} /> Policies
                    </button>
                    {e.status === "published" && (
                      <button onClick={() => onUnpublish(e.id)} className="text-xs text-[var(--gt-accent)] hover:underline" data-testid={`admin-unpublish-${e.id}`}>
                        Unpublish
                      </button>
                    )}
                    <button
                      onClick={() => setTransferEvent(e)}
                      className="text-xs text-[var(--gt-ink-2)] hover:underline inline-flex items-center gap-1"
                      data-testid={`admin-transfer-owner-${e.id}`}
                      title="Transfer Ownership"
                    >
                      <RefreshCw size={12} /> Ownership
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {events.length === 0 && (
              <tr><td colSpan={5} className="py-10 text-center text-sm text-[var(--gt-ink-3)]">No events.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Sprint AO — per-event fees dialog */}
      {feesEvent && <AdminEventFeesDialog event={feesEvent} onClose={() => setFeesEvent(null)} />}

      {/* Sprint T1 — admin add-ons manager */}
      <Dialog open={!!addonsEvent} onOpenChange={(o) => !o && setAddonsEvent(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="admin-addons-dialog">
          <DialogHeader>
            <DialogTitle>Add-ons — {addonsEvent?.title}</DialogTitle>
            <DialogDescription>Optional extras buyers can purchase with tickets on this event.</DialogDescription>
          </DialogHeader>
          {addonsEvent && <AddonsManager eventId={addonsEvent.id} />}
        </DialogContent>
      </Dialog>

      {/* Sprint S — admin policies editor */}
      <Dialog open={!!policyEvent} onOpenChange={(o) => !o && setPolicyEvent(null)}>
        <DialogContent className="max-w-xl" data-testid="admin-policies-dialog">
          <DialogHeader>
            <DialogTitle>Event policies — {policyEvent?.title}</DialogTitle>
            <DialogDescription>
              Shown as collapsibles on the public event page. Leave blank to hide a section
              (paid events show a default ACL-aligned refund statement when refund policy is blank).
            </DialogDescription>
          </DialogHeader>
          {policyLoading ? (
            <div className="py-8 flex justify-center"><Loader2 className="animate-spin" size={20} /></div>
          ) : (
            <div className="space-y-4">
              {[
                { key: "event_terms", label: "Event terms", tid: "admin-event-terms-input" },
                { key: "refund_policy", label: "Refund policy", tid: "admin-refund-policy-input" },
                { key: "event_disclaimer", label: "Disclaimer", tid: "admin-event-disclaimer-input" },
              ].map((f) => (
                <div key={f.key}>
                  <label className="gt-form-label">{f.label}</label>
                  <textarea
                    className="gt-input min-h-[80px]"
                    value={policyForm[f.key]}
                    maxLength={10000}
                    onChange={(ev) => setPolicyForm({ ...policyForm, [f.key]: ev.target.value })}
                    data-testid={f.tid}
                  />
                </div>
              ))}
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" className="gt-btn-ghost text-sm" onClick={() => setPolicyEvent(null)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="gt-btn-primary text-sm"
                  onClick={savePolicies}
                  disabled={policySaving}
                  data-testid="admin-policies-save-btn"
                >
                  {policySaving ? <Loader2 size={14} className="animate-spin" /> : null}
                  Save policies
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Sprint AW — Transfer Ownership dialog */}
      {transferEvent && (
        <TransferOwnershipDialog
          event={transferEvent}
          onClose={() => setTransferEvent(null)}
          onDone={() => { setTransferEvent(null); onOwnershipTransferred && onOwnershipTransferred(); }}
        />
      )}

      {/* Sprint AW — Admin create-event modal (replaces window.prompt) */}
      {createOpen && (
        <AdminCreateEventDialog
          onClose={() => setCreateOpen(false)}
          onDone={() => { setCreateOpen(false); onOwnershipTransferred && onOwnershipTransferred(); }}
        />
      )}
    </div>
  );
}

// Sprint AW — TransferOwnershipDialog moved to /components/TransferOwnershipDialog.jsx (shared between Admin Events tab and Event Team page). Import at top of file.


// Sprint AW — Admin Create Event modal (replaces window.prompt flow)
function AdminCreateEventDialog({ onClose, onDone }) {
  const AU_STATES = ["NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT"];
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [owner, setOwner] = useState(null);
  const [form, setForm] = useState({
    title: "",
    venue_name: "",
    venue_address: "",
    state: "NSW",
    start_at: "",
  });
  const [saving, setSaving] = useState(false);
  const [searching, setSearching] = useState(false);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const search = async () => {
    if (!q.trim()) return;
    setSearching(true);
    try { setResults((await adminApi.accounts(q.trim())) || []); }
    catch (e) { toast.error(extractError(e)); }
    finally { setSearching(false); }
  };

  const submit = async () => {
    if (!owner) { toast.error("Please select an Event Owner."); return; }
    if (!form.title.trim() || !form.venue_name.trim() || !form.venue_address.trim() || !form.start_at) {
      toast.error("Please complete all event fields.");
      return;
    }
    let startIso;
    try { startIso = new Date(form.start_at).toISOString(); }
    catch { toast.error("Invalid start date/time."); return; }
    setSaving(true);
    try {
      await adminApi.createEventForOrganiser({
        organiser_id: owner.id,
        title: form.title.trim(),
        venue_name: form.venue_name.trim(),
        venue_address: form.venue_address.trim(),
        state: form.state,
        start_at: startIso,
        visibility: "public",
        ticket_types: [{ name: "General", price_cents: 0, quantity: 100 }],
      });
      toast.success(`Event created under ${owner.email}`);
      onDone();
    } catch (e) { toast.error(extractError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="admin-create-event-dialog">
        <DialogHeader>
          <DialogTitle>Create event for organiser</DialogTitle>
          <DialogDescription>
            Assigns the selected organiser as the Event Owner. They will immediately see the event in their My Events.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <div>
            <label className="gt-form-label">Event Owner (search organiser)</label>
            <div className="flex gap-2">
              <input
                className="gt-input flex-1"
                placeholder="Email, name or business name"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); search(); } }}
                data-testid="create-event-owner-search"
              />
              <button type="button" onClick={search} disabled={searching} className="gt-btn-ghost text-sm" data-testid="create-event-owner-search-btn">
                {searching ? <Loader2 size={14} className="animate-spin" /> : "Search"}
              </button>
            </div>
            {results.length > 0 && (
              <div className="mt-2 max-h-40 overflow-y-auto border border-[var(--gt-line)] rounded-sm">
                {results.map((o) => (
                  <button
                    type="button"
                    key={o.id}
                    onClick={() => setOwner(o)}
                    className={`w-full text-left px-3 py-2 text-xs border-b border-[var(--gt-line)] last:border-b-0 hover:bg-[var(--gt-line)] ${owner?.id === o.id ? "bg-[var(--gt-line)]" : ""}`}
                    data-testid={`create-event-owner-result-${o.id}`}
                  >
                    <div className="font-semibold">{o.name || o.business_name || "—"}</div>
                    <div className="font-mono text-[var(--gt-ink-3)]">{o.email}</div>
                  </button>
                ))}
              </div>
            )}
            {owner && (
              <div className="mt-2 gt-card p-2 border-l-4 border-emerald-600 text-xs" data-testid="create-event-owner-selected">
                Selected: <strong>{owner.name || owner.business_name}</strong> · {owner.email}
              </div>
            )}
          </div>
          <div>
            <label className="gt-form-label">Event title</label>
            <input className="gt-input" value={form.title} onChange={upd("title")} data-testid="create-event-title-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="gt-form-label">Venue name</label>
              <input className="gt-input" value={form.venue_name} onChange={upd("venue_name")} data-testid="create-event-venue-name-input" />
            </div>
            <div>
              <label className="gt-form-label">State</label>
              <select className="gt-input" value={form.state} onChange={upd("state")} data-testid="create-event-state-select">
                {AU_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="gt-form-label">Venue address</label>
            <input className="gt-input" value={form.venue_address} onChange={upd("venue_address")} data-testid="create-event-venue-address-input" />
          </div>
          <div>
            <label className="gt-form-label">Start date &amp; time</label>
            <input type="datetime-local" className="gt-input" value={form.start_at} onChange={upd("start_at")} data-testid="create-event-start-input" />
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="gt-btn-ghost text-sm" data-testid="create-event-cancel-btn">Cancel</button>
            <button type="button" onClick={submit} disabled={saving} className="gt-btn-primary text-sm" data-testid="create-event-submit-btn">
              {saving ? <Loader2 size={14} className="animate-spin" /> : null}
              Create event
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ConfigPanel({ config, onSave }) {
  // Sprint AT — real live pricing engine fields (replaces legacy platform_fee_* dead fields).
  const [scRate, setScRate] = useState(String(((config.service_charge_rate_bps ?? 250) / 100)));
  const [scFlat, setScFlat] = useState(String((config.service_charge_per_ticket_cents ?? 30)));
  const [gstEnabled, setGstEnabled] = useState(config.service_fee_gst_enabled !== false);
  const [gstRate, setGstRate] = useState(String(((config.gst_rate_bps ?? 1000) / 100)));
  const [defaultSc, setDefaultSc] = useState(config.default_service_charge_paid_by || "customer");

  const submit = (e) => {
    e.preventDefault();
    const scRateNum = parseFloat(scRate);
    const scFlatNum = parseInt(scFlat, 10);
    const gstRateNum = parseFloat(gstRate);
    if ([scRateNum, gstRateNum].some((n) => Number.isNaN(n) || n < 0) || scRateNum > 100 || gstRateNum > 100) {
      toast.error("Service fee % and GST rate must be 0–100.");
      return;
    }
    if (Number.isNaN(scFlatNum) || scFlatNum < 0 || scFlatNum > 10000) {
      toast.error("Per-ticket fee must be 0–10000 cents.");
      return;
    }
    onSave({
      service_charge_rate_bps: Math.round(scRateNum * 100),
      service_charge_per_ticket_cents: scFlatNum,
      service_fee_gst_enabled: !!gstEnabled,
      gst_rate_bps: Math.round(gstRateNum * 100),
      default_service_charge_paid_by: defaultSc,
    });
  };

  return (
    <form onSubmit={submit} className="gt-card max-w-xl" data-testid="admin-config-panel">
      <div className="mb-4">
        <div className="gt-label">Sprint AT · Platform fee &amp; GST (live pricing engine)</div>
        <p className="text-xs text-[var(--gt-ink-2)] mt-1">
          These values control the Getickets service fee and Service Fee GST buyers see on every event.
          Organisers cannot edit them — this is the single source of truth.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="gt-form-label" htmlFor="cfg-sc-percent">Service fee percentage</label>
          <div className="relative">
            <input
              id="cfg-sc-percent"
              type="number" step="0.1" min="0" max="100"
              className="gt-input pr-8 font-mono"
              value={scRate}
              onChange={(e) => setScRate(e.target.value)}
              data-testid="admin-config-service-fee-percent"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[var(--gt-ink-3)]">%</span>
          </div>
        </div>
        <div>
          <label className="gt-form-label" htmlFor="cfg-sc-flat">Service fee per-ticket fee</label>
          <div className="relative">
            <input
              id="cfg-sc-flat"
              type="number" min="0" max="10000" step="1"
              className="gt-input pr-14 font-mono"
              value={scFlat}
              onChange={(e) => setScFlat(e.target.value)}
              data-testid="admin-config-service-fee-per-ticket"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[var(--gt-ink-3)]">cents</span>
          </div>
          <p className="text-[10px] text-[var(--gt-ink-3)] mt-1">A${(parseInt(scFlat, 10) || 0) / 100} per ticket</p>
        </div>

        <div className="col-span-2 flex items-center justify-between border-t border-[var(--gt-line)] pt-4">
          <div>
            <label className="gt-form-label mb-0">Service Fee GST enabled</label>
            <p className="text-[10px] text-[var(--gt-ink-3)] mt-1">Applies GST to the Getickets service fee (never to the ticket price).</p>
          </div>
          <button
            type="button"
            onClick={() => setGstEnabled((v) => !v)}
            className={`px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] rounded-sm border transition-colors ${
              gstEnabled ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-[var(--gt-ink-2)] border-[var(--gt-line)]"
            }`}
            data-testid="admin-config-service-fee-gst-toggle"
            aria-pressed={gstEnabled}
          >
            {gstEnabled ? "GST ON" : "GST OFF"}
          </button>
        </div>

        <div>
          <label className="gt-form-label" htmlFor="cfg-gst-rate">Service Fee GST rate</label>
          <div className="relative">
            <input
              id="cfg-gst-rate"
              type="number" step="0.1" min="0" max="100"
              className="gt-input pr-8 font-mono disabled:opacity-50"
              value={gstRate}
              onChange={(e) => setGstRate(e.target.value)}
              disabled={!gstEnabled}
              data-testid="admin-config-gst-rate"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[var(--gt-ink-3)]">%</span>
          </div>
        </div>
        <div>
          <label className="gt-form-label" htmlFor="cfg-default-sc">Default service fee paid by</label>
          <select
            id="cfg-default-sc"
            className="gt-input"
            value={defaultSc}
            onChange={(e) => setDefaultSc(e.target.value)}
            data-testid="admin-config-default-sc-paid-by"
          >
            <option value="customer">Customer</option>
            <option value="included">Organiser absorbs (included in ticket price)</option>
          </select>
          <p className="text-[10px] text-[var(--gt-ink-3)] mt-1">Service Fee GST always follows this setting.</p>
        </div>
      </div>

      <div className="mt-5 text-xs text-[var(--gt-ink-3)]">
        <AlertTriangle size={12} className="inline mr-1 text-[var(--gt-accent)]" />
        Changes apply to <strong>new</strong> orders only. Historic orders keep their stored per-order snapshots — they are never recalculated.
      </div>
      <div className="mt-5 flex items-center gap-4">
        <button type="submit" className="gt-btn-primary text-sm" data-testid="admin-config-save-btn">Save changes</button>
        <div className="text-[10px] uppercase tracking-widest text-[var(--gt-ink-3)]">
          Last updated {formatAuDate(config.updated_at)}{config.updated_by ? ` · by ${String(config.updated_by).slice(0, 8)}` : ""}
        </div>
      </div>
    </form>
  );
}

function AuditPanel({ logs }) {
  return (
    <div className="gt-card" data-testid="admin-audit-panel">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-widest text-[var(--gt-ink-3)] border-b-2 border-[var(--gt-ink)]">
            <th className="py-2 pr-3">When</th>
            <th className="py-2 pr-3">Action</th>
            <th className="py-2 pr-3">Organiser</th>
            <th className="py-2 pl-3">Meta</th>
          </tr>
        </thead>
        <tbody>
          {logs.map(l => (
            <tr key={l.id} className="border-b border-[var(--gt-line)]" data-testid={`admin-audit-row-${l.id}`}>
              <td className="py-2 pr-3 text-xs text-[var(--gt-ink-2)]">{formatAuDate(l.created_at)}</td>
              <td className="py-2 pr-3 font-mono text-xs">{l.action}</td>
              <td className="py-2 pr-3 font-mono text-[10px]">{(l.organiser_id || "").slice(0, 8) || "—"}</td>
              <td className="py-2 pl-3 text-[11px] font-mono text-[var(--gt-ink-2)]">{JSON.stringify(l.meta)}</td>
            </tr>
          ))}
          {logs.length === 0 && (
            <tr><td colSpan={4} className="py-10 text-center text-sm text-[var(--gt-ink-3)]">No audit logs yet.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}


// ---------------------------- Sprint BF — Admin Orders & Tickets panel ----------------------------
function AdminOrdersPanel() {
  const [mode, setMode] = useState("orders"); // "orders" | "tickets"
  const [q, setQ] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    orderStatus: "",
    ticketStatus: "",
    checkedIn: "",     // "" | "true" | "false"
    dateFrom: "",
    dateTo: "",
    eventDateFrom: "",
    eventDateTo: "",
    buyerPhone: "",
    organiserId: "",
    eventId: "",
  });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ items: [], total: 0, pages: 0, page: 1, per_page: 25 });
  const [error, setError] = useState("");

  const paramsForApi = () => ({
    q: q || undefined,
    order_status: filters.orderStatus || undefined,
    ticket_status: mode === "tickets" ? (filters.ticketStatus || undefined) : undefined,
    checked_in: filters.checkedIn !== "" ? filters.checkedIn === "true" : undefined,
    date_from: filters.dateFrom || undefined,
    date_to: filters.dateTo || undefined,
    event_date_from: filters.eventDateFrom || undefined,
    event_date_to: filters.eventDateTo || undefined,
    buyer_phone: filters.buyerPhone || undefined,
    organiser_id: filters.organiserId || undefined,
    event_id: filters.eventId || undefined,
  });

  const load = useCallback(async (nextPage = page) => {
    setLoading(true); setError("");
    try {
      const params = { ...paramsForApi(), page: nextPage, per_page: 25 };
      const res = mode === "tickets"
        ? await adminApi.searchTickets(params)
        : await adminApi.searchOrders(params);
      setData(res); setPage(res.page);
    } catch (e) {
      setError(extractError(e));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, q, filters, page]);

  useEffect(() => { load(1); /* eslint-disable-next-line */ }, [mode]);
  // Sprint BF — auto-apply advanced filters so users don't have to click Search.
  // Debounce briefly so typing in the free-text search box doesn't fire on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => { load(1); }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);
  const onSearch = (e) => { e?.preventDefault(); load(1); };
  const patchFilter = (k, v) => setFilters((prev) => ({ ...prev, [k]: v }));

  return (
    <div className="space-y-4" data-testid="admin-orders-panel">
      {/* Mode toggle */}
      <div className="flex items-center gap-2">
        <div className="inline-flex border-2 border-[var(--gt-ink)]" role="tablist">
          <button
            type="button"
            className={`px-4 py-1.5 text-xs uppercase tracking-widest font-bold ${mode === "orders" ? "bg-[var(--gt-ink)] text-white" : ""}`}
            onClick={() => { setMode("orders"); setPage(1); }}
            data-testid="admin-mode-orders"
          >Orders</button>
          <button
            type="button"
            className={`px-4 py-1.5 text-xs uppercase tracking-widest font-bold border-l-2 border-[var(--gt-ink)] ${mode === "tickets" ? "bg-[var(--gt-ink)] text-white" : ""}`}
            onClick={() => { setMode("tickets"); setPage(1); }}
            data-testid="admin-mode-tickets"
          >Tickets</button>
        </div>
        <div className="text-xs text-[var(--gt-ink-3)]">
          {mode === "orders"
            ? "Search across all organisers by order id, ticket code, buyer email/name/phone, event, organiser."
            : "Search individual tickets by ticket code / id / buyer / event / organiser."}
        </div>
      </div>

      <form onSubmit={onSearch} className="flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[240px]">
          <label className="gt-label mb-1 block">Search</label>
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={mode === "tickets"
              ? "Ticket code, ticket id, order id, buyer email/name/phone, event…"
              : "Order id, ticket code, buyer email/name/phone, event, organiser…"}
            className="gt-input w-full"
            data-testid="admin-orders-search-input"
          />
        </div>
        <button type="submit" className="gt-btn-primary text-sm" disabled={loading} data-testid="admin-orders-search-btn">
          {loading ? "Loading…" : "Search"}
        </button>
        <button
          type="button"
          className="gt-btn-ghost text-xs"
          onClick={() => setShowFilters((s) => !s)}
          data-testid="admin-orders-toggle-filters"
        >
          {showFilters ? "Hide filters" : "Advanced filters"}
        </button>
      </form>

      {showFilters && (
        <div className="border border-[var(--gt-line)] p-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs bg-[var(--gt-bg-marketing)]" data-testid="admin-orders-filters">
          <div>
            <label className="gt-label mb-1 block">Order status</label>
            <select className="gt-input w-full" value={filters.orderStatus} onChange={(e) => patchFilter("orderStatus", e.target.value)} data-testid="filter-order-status">
              <option value="">All</option>
              <option value="paid">Paid</option>
              <option value="pending">Pending</option>
              <option value="refunded">Refunded</option>
              <option value="partial_refund">Partial refund</option>
              <option value="voided">Voided</option>
            </select>
          </div>
          {mode === "tickets" && (
            <div>
              <label className="gt-label mb-1 block">Ticket status</label>
              <select className="gt-input w-full" value={filters.ticketStatus} onChange={(e) => patchFilter("ticketStatus", e.target.value)} data-testid="filter-ticket-status">
                <option value="">All</option>
                <option value="valid">Valid</option>
                <option value="checked_in">Checked in</option>
                <option value="refunded">Refunded</option>
                <option value="voided">Voided</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          )}
          <div>
            <label className="gt-label mb-1 block">Checked in</label>
            <select className="gt-input w-full" value={filters.checkedIn} onChange={(e) => patchFilter("checkedIn", e.target.value)} data-testid="filter-checked-in">
              <option value="">Any</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </div>
          <div>
            <label className="gt-label mb-1 block">Buyer phone</label>
            <input type="text" className="gt-input w-full" value={filters.buyerPhone} onChange={(e) => patchFilter("buyerPhone", e.target.value)} placeholder="0412 345 678" data-testid="filter-buyer-phone" />
          </div>
          <div>
            <label className="gt-label mb-1 block">Order date from</label>
            <input type="date" className="gt-input w-full" value={filters.dateFrom} onChange={(e) => patchFilter("dateFrom", e.target.value)} data-testid="filter-order-date-from" />
          </div>
          <div>
            <label className="gt-label mb-1 block">Order date to</label>
            <input type="date" className="gt-input w-full" value={filters.dateTo} onChange={(e) => patchFilter("dateTo", e.target.value)} data-testid="filter-order-date-to" />
          </div>
          <div>
            <label className="gt-label mb-1 block">Event date from</label>
            <input type="date" className="gt-input w-full" value={filters.eventDateFrom} onChange={(e) => patchFilter("eventDateFrom", e.target.value)} data-testid="filter-event-date-from" />
          </div>
          <div>
            <label className="gt-label mb-1 block">Event date to</label>
            <input type="date" className="gt-input w-full" value={filters.eventDateTo} onChange={(e) => patchFilter("eventDateTo", e.target.value)} data-testid="filter-event-date-to" />
          </div>
          <div>
            <label className="gt-label mb-1 block">Organiser ID</label>
            <input type="text" className="gt-input w-full font-mono text-[10px]" value={filters.organiserId} onChange={(e) => patchFilter("organiserId", e.target.value)} data-testid="filter-organiser-id" />
          </div>
          <div>
            <label className="gt-label mb-1 block">Event ID</label>
            <input type="text" className="gt-input w-full font-mono text-[10px]" value={filters.eventId} onChange={(e) => patchFilter("eventId", e.target.value)} data-testid="filter-event-id" />
          </div>
        </div>
      )}

      {error && <div className="text-sm text-[var(--gt-accent)]" data-testid="admin-orders-error">{error}</div>}

      <div className="border-2 border-[var(--gt-ink)] bg-white overflow-x-auto">
        {mode === "orders" ? <OrdersTable data={data} loading={loading} /> : <TicketsTable data={data} loading={loading} />}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-[var(--gt-ink-2)]">
        <div data-testid="admin-orders-total">
          Total: {data.total} {mode === "orders" ? "orders" : "tickets"}
        </div>
        <div className="flex items-center gap-2">
          <button type="button" className="gt-btn-ghost text-xs" disabled={loading || page <= 1} onClick={() => load(page - 1)} data-testid="admin-orders-prev-page">Prev</button>
          <span data-testid="admin-orders-page">Page {data.page} / {Math.max(1, data.pages)}</span>
          <button type="button" className="gt-btn-ghost text-xs" disabled={loading || page >= data.pages} onClick={() => load(page + 1)} data-testid="admin-orders-next-page">Next</button>
        </div>
      </div>
    </div>
  );
}

function OrdersTable({ data, loading }) {
  return (
    <table className="w-full text-sm" data-testid="admin-orders-table">
      <thead className="bg-[var(--gt-bg-marketing)]">
        <tr className="text-left text-[10px] uppercase tracking-widest text-[var(--gt-ink-2)]">
          <th className="py-2 px-3">Order</th>
          <th className="py-2 px-3">Buyer</th>
          <th className="py-2 px-3">Event</th>
          <th className="py-2 px-3">Organiser</th>
          <th className="py-2 px-3">Date</th>
          <th className="py-2 px-3 text-right">Tickets</th>
          <th className="py-2 px-3 text-right">Total</th>
          <th className="py-2 px-3">Status</th>
          <th className="py-2 px-3 text-right">Actions</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-[var(--gt-line)]">
        {(data.items || data.orders || []).map((o) => (
          <tr key={o.id} data-testid={`admin-order-row-${o.id}`}>
            <td className="py-2 px-3 font-mono text-xs break-all max-w-[140px]">{o.id.slice(0, 8)}…</td>
            <td className="py-2 px-3">
              <div className="font-semibold text-xs">{o.buyer_name}</div>
              <div className="text-[10px] text-[var(--gt-ink-2)]">{o.buyer_email}</div>
              {o.buyer_phone && (
                <div className="text-[10px] text-[var(--gt-ink-2)]" data-testid={`admin-order-phone-${o.id}`}>{o.buyer_phone}</div>
              )}
            </td>
            <td className="py-2 px-3 text-xs">{o.event?.title || o.event_title}</td>
            <td className="py-2 px-3 text-xs">
              <div>{o.organiser?.business_name || o.organiser?.name || o.organiser_name}</div>
              <div className="text-[10px] text-[var(--gt-ink-2)]">{o.organiser?.email || o.organiser_email}</div>
            </td>
            <td className="py-2 px-3 text-xs">{formatAuDate(o.created_at)}</td>
            <td className="py-2 px-3 text-right font-mono text-xs">{o.tickets_count ?? o.ticket_count}</td>
            <td className="py-2 px-3 text-right font-mono text-xs font-bold">{centsToAUD(o.amount_total_cents)}</td>
            <td className="py-2 px-3 text-xs">{o.status}</td>
            <td className="py-2 px-3 text-right">
              <Link to={`/dashboard/admin/orders/${o.id}`} className="gt-btn-ghost text-[10px]" data-testid={`admin-order-view-${o.id}`}>
                Open
              </Link>
            </td>
          </tr>
        ))}
        {(!(data.items || data.orders) || (data.items || data.orders).length === 0) && !loading && (
          <tr><td colSpan={9} className="py-8 text-center text-sm text-[var(--gt-ink-3)]" data-testid="admin-orders-empty">
            No orders found.
          </td></tr>
        )}
      </tbody>
    </table>
  );
}

function TicketsTable({ data, loading }) {
  return (
    <table className="w-full text-sm" data-testid="admin-tickets-table">
      <thead className="bg-[var(--gt-bg-marketing)]">
        <tr className="text-left text-[10px] uppercase tracking-widest text-[var(--gt-ink-2)]">
          <th className="py-2 px-3">Ticket</th>
          <th className="py-2 px-3">Order / Buyer</th>
          <th className="py-2 px-3">Event</th>
          <th className="py-2 px-3">Organiser</th>
          <th className="py-2 px-3">Seat</th>
          <th className="py-2 px-3">Status</th>
          <th className="py-2 px-3 text-right">Actions</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-[var(--gt-line)]">
        {(data.items || []).map((t) => {
          const seat = t.seat_label
            ? `${t.section || ""}${t.row_label ? " Row " + t.row_label : ""} ${t.seat_label}`.trim()
            : (t.ticket_type_name || "GA");
          return (
            <tr key={t.id} data-testid={`admin-ticket-row-${t.code}`}>
              <td className="py-2 px-3 font-mono text-xs">{t.code}</td>
              <td className="py-2 px-3 text-xs">
                <div>{t.order?.buyer_name}</div>
                <div className="text-[10px] text-[var(--gt-ink-2)]">{t.order?.buyer_email}</div>
                {t.order?.buyer_phone && (
                  <div className="text-[10px] text-[var(--gt-ink-2)]" data-testid={`admin-ticket-phone-${t.code}`}>{t.order.buyer_phone}</div>
                )}
              </td>
              <td className="py-2 px-3 text-xs">{t.event?.title}</td>
              <td className="py-2 px-3 text-xs">{t.organiser?.business_name || t.organiser?.name}</td>
              <td className="py-2 px-3 text-xs">{seat}</td>
              <td className="py-2 px-3 text-xs">
                <span
                  className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 border ${t.is_active ? "border-[var(--gt-line)]" : "border-[var(--gt-accent)] text-[var(--gt-accent)]"}`}
                  data-testid={`admin-ticket-status-badge-${t.code}`}
                >{t.status}</span>
                {t.checked_in_at && (
                  <div className="text-[10px] text-[var(--gt-ink-2)] mt-0.5">Checked in {formatAuDate(t.checked_in_at)}</div>
                )}
              </td>
              <td className="py-2 px-3 text-right whitespace-nowrap">
                <Link to={`/dashboard/admin/tickets/${t.id}`} className="gt-btn-ghost text-[10px] mr-1" data-testid={`admin-ticket-open-${t.code}`}>
                  Open
                </Link>
                <Link to={`/dashboard/admin/orders/${t.order?.id}`} className="gt-btn-ghost text-[10px]" data-testid={`admin-ticket-open-order-${t.code}`}>
                  Order
                </Link>
              </td>
            </tr>
          );
        })}
        {(!data.items || data.items.length === 0) && !loading && (
          <tr><td colSpan={7} className="py-8 text-center text-sm text-[var(--gt-ink-3)]" data-testid="admin-tickets-empty">
            No tickets found.
          </td></tr>
        )}
      </tbody>
    </table>
  );
}
